import { useState } from "react";
import {
  ArrowRightLeft,
  CheckCircle2,
  Circle,
  GitMerge,
  Home,
  History,
  IdCard,
  Link2,
  ListTodo,
  Pencil,
  Plus,
  RotateCcw,
  StickyNote,
  Unlink,
  UserMinus,
  UserPlus,
  type LucideIcon,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDataProvider, useNotify } from "ra-core";

import { usePermission } from "../providers/usePermission";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

import type {
  CrmDataProvider,
  NewTask,
  Task,
  TimelineEntry,
  TimelineResource,
} from "../providers/types";
import { EmptyState } from "./GraphStatus";

interface GraphTimelineProps {
  resource: TimelineResource;
  id: string;
}

/**
 * Tasks and the timeline for one contact, company or deal (W8 D2). The
 * timeline is the database's: notes, task events and, for deals, stage
 * history, newest first. Adding or changing anything refetches it.
 */
export function GraphTimeline({ resource, id }: GraphTimelineProps) {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const notify = useNotify();
  const queryKey = ["timeline", resource, id];
  // Read-only roles see tasks and the timeline without the controls (W9 F4).
  const canWrite = usePermission("notes_tasks");
  const { data, isPending, error } = useQuery({
    queryKey,
    queryFn: () => dataProvider.getTimeline(resource, id),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey });
    // Home's Open tasks view reads the same tasks.
    void queryClient.invalidateQueries({ queryKey: ["saved_views"] });
  };
  const onError = (message: string) => () => notify(message, { type: "error" });

  const addNote = useMutation({
    mutationFn: (body: string) => dataProvider.addNote(resource, id, body),
    onSuccess: refresh,
    onError: onError("The note could not be saved"),
  });
  const addTask = useMutation({
    mutationFn: (task: NewTask) => dataProvider.addTask(resource, id, task),
    onSuccess: refresh,
    onError: onError("The task could not be added"),
  });
  const toggleTask = useMutation({
    mutationFn: ({
      taskId,
      completed,
    }: {
      taskId: string;
      completed: boolean;
    }) => dataProvider.setTaskCompleted(taskId, completed),
    onSuccess: refresh,
    onError: onError("The task could not be updated"),
  });
  const editNote = useMutation({
    mutationFn: ({ noteId, body }: { noteId: string; body: string }) =>
      dataProvider.editNote(noteId, body),
    onSuccess: refresh,
    onError: onError("The note could not be saved"),
  });

  if (isPending) {
    return (
      <div className="space-y-4" aria-busy="true">
        <span className="sr-only">Loading timeline…</span>
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="size-7 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3.5 w-1/3" />
              <Skeleton className="h-3.5 w-2/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (error || !data) {
    return (
      <p className="text-sm text-muted-foreground" role="alert">
        The timeline could not be loaded.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <TaskList
        tasks={data.tasks}
        onToggle={(taskId, completed) =>
          toggleTask.mutate({ taskId, completed })
        }
        onAdd={(task) => addTask.mutate(task)}
        busy={addTask.isPending || toggleTask.isPending}
        canWrite={canWrite}
      />
      <section>
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Timeline
        </h2>
        {canWrite ? (
          <NoteForm
            onAdd={(body) => addNote.mutate(body)}
            busy={addNote.isPending}
          />
        ) : null}
        {data.entries.length === 0 ? (
          <EmptyState icon={History} title="Nothing yet" className="py-8">
            Notes, stage moves and edits show up here.
          </EmptyState>
        ) : (
          <ol className="relative mt-4 space-y-4" aria-label="Timeline">
            {data.entries.map((entry) => (
              <TimelineItem
                key={entry.id}
                entry={entry}
                canEdit={canWrite}
                onEditNote={(body) =>
                  entry.ref_id &&
                  editNote.mutate({ noteId: entry.ref_id, body })
                }
              />
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}

/** The task_priorities catalog (0023), most urgent first. */
const PRIORITIES = [
  ["urgent", "Urgent"],
  ["high", "High"],
  ["normal", "Normal"],
  ["low", "Low"],
] as const;

function TaskList({
  tasks,
  onToggle,
  onAdd,
  busy,
  canWrite,
}: {
  tasks: Task[];
  onToggle: (taskId: string, completed: boolean) => void;
  onAdd: (task: NewTask) => void;
  busy: boolean;
  canWrite: boolean;
}) {
  const [title, setTitle] = useState("");
  const [dueOn, setDueOn] = useState("");
  const [priority, setPriority] = useState("normal");
  return (
    <section>
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Tasks
      </h2>
      {tasks.length === 0 ? (
        <p className="text-sm text-muted-foreground">None</p>
      ) : (
        <ul className="space-y-2" aria-label="Tasks">
          {tasks.map((task) => {
            const done = task.completed_at !== null;
            return (
              <li key={task.id} className="flex items-center gap-2">
                <Checkbox
                  checked={done}
                  disabled={busy || !canWrite}
                  onCheckedChange={(checked) =>
                    onToggle(task.id, checked === true)
                  }
                  aria-label={`${done ? "Reopen" : "Complete"} ${task.title}`}
                />
                <span
                  className={done ? "line-through text-muted-foreground" : ""}
                >
                  {task.title}
                </span>
                {task.priority === "urgent" || task.priority === "high" ? (
                  <span
                    className={
                      task.priority === "urgent"
                        ? "rounded bg-destructive/10 px-1.5 text-xs font-medium text-destructive"
                        : "rounded bg-muted px-1.5 text-xs font-medium"
                    }
                  >
                    {task.priority}
                  </span>
                ) : null}
                {task.due_on ? (
                  <span className="text-sm text-muted-foreground">
                    due {task.due_on}
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {canWrite ? (
        <form
          className="mt-3 flex flex-wrap gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (!title.trim()) return;
            onAdd({ title: title.trim(), due_on: dueOn || null, priority });
            setTitle("");
            setDueOn("");
            setPriority("normal");
          }}
        >
          <Input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="New task"
            aria-label="New task"
            className="w-64"
          />
          <Input
            type="date"
            value={dueOn}
            onChange={(event) => setDueOn(event.target.value)}
            aria-label="Due date"
            className="w-40"
          />
          <select
            value={priority}
            onChange={(event) => setPriority(event.target.value)}
            aria-label="Priority"
            className="h-9 rounded-md border border-input bg-transparent px-2 text-sm"
          >
            {PRIORITIES.map(([id, label]) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
          <Button
            type="submit"
            variant="outline"
            disabled={busy || !title.trim()}
          >
            Add task
          </Button>
        </form>
      ) : null}
    </section>
  );
}

function NoteForm({
  onAdd,
  busy,
}: {
  onAdd: (body: string) => void;
  busy: boolean;
}) {
  const [body, setBody] = useState("");
  return (
    <form
      className="space-y-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (!body.trim()) return;
        onAdd(body.trim());
        setBody("");
      }}
    >
      <Textarea
        value={body}
        onChange={(event) => setBody(event.target.value)}
        placeholder="Add a note"
        aria-label="Add a note"
      />
      <Button type="submit" variant="outline" disabled={busy || !body.trim()}>
        Add note
      </Button>
    </form>
  );
}

const KIND_LABEL: Record<string, string> = {
  note_added: "Note",
  task_created: "Task added",
  task_completed: "Task done",
  task_reopened: "Task reopened",
  stage_change: "Stage",
  // Written by the 0012 triggers (W10 H1); the entry's body says what changed.
  created: "Created",
  details_changed: "Details",
  field_changed: "Field",
  link_added: "Link",
  link_ended: "Link ended",
  member_added: "Member",
  member_ended: "Member left",
  // 0013-0014 (W10 H2-H3) and merges.
  party_added: "Party",
  party_ended: "Party left",
  identifier_added: "Identifier",
  identifier_removed: "Identifier removed",
  type_added: "Type",
  type_removed: "Type removed",
  merge: "Merge",
  // 0017 (W14): a deal gaining, losing or changing its primary property.
  property_changed: "Property",
  // 0018 (W15 M2) and 0020 (M4).
  loan_added: "Loan",
  loan_removed: "Loan removed",
  license_added: "License",
  license_removed: "License removed",
  license_changed: "License",
  // 0021 (W15 M5).
  assigned: "Team",
  unassigned: "Team",
  // 0024 (W15 M8).
  condition_added: "Condition",
  condition_changed: "Condition",
  milestone_done: "Milestone",
};

/** The icon on the timeline rail for each kind of entry (W12 U2). */
const KIND_ICON: Record<string, LucideIcon> = {
  note_added: StickyNote,
  task_created: ListTodo,
  task_completed: CheckCircle2,
  task_reopened: RotateCcw,
  stage_change: ArrowRightLeft,
  created: Plus,
  details_changed: Pencil,
  field_changed: Pencil,
  link_added: Link2,
  link_ended: Unlink,
  member_added: UserPlus,
  member_ended: UserMinus,
  party_added: UserPlus,
  party_ended: UserMinus,
  identifier_added: IdCard,
  identifier_removed: IdCard,
  type_added: IdCard,
  type_removed: IdCard,
  merge: GitMerge,
  property_changed: Home,
  loan_added: Plus,
  loan_removed: Unlink,
  license_added: IdCard,
  license_removed: IdCard,
  license_changed: IdCard,
  assigned: UserPlus,
  unassigned: UserMinus,
  condition_added: ListTodo,
  condition_changed: CheckCircle2,
  milestone_done: CheckCircle2,
};

const RELATIVE = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

/** "just now", "5 minutes ago", "yesterday", "3 days ago"; a date after a month. */
function relativeTime(at: string): string {
  const seconds = (new Date(at).getTime() - Date.now()) / 1000;
  const minutes = seconds / 60;
  const hours = minutes / 60;
  const days = hours / 24;
  if (Math.abs(seconds) < 60) return "just now";
  if (Math.abs(minutes) < 60)
    return RELATIVE.format(Math.round(minutes), "minute");
  if (Math.abs(hours) < 24) return RELATIVE.format(Math.round(hours), "hour");
  if (Math.abs(days) < 30) return RELATIVE.format(Math.round(days), "day");
  return new Date(at).toLocaleDateString(undefined, { dateStyle: "medium" });
}

function TimelineItem({
  entry,
  canEdit,
  onEditNote,
}: {
  entry: TimelineEntry;
  canEdit: boolean;
  onEditNote: (body: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(entry.note_body ?? "");
  const text =
    entry.kind === "note_added"
      ? entry.note_body
      : entry.ref_type === "task"
        ? entry.task_title
        : entry.body;
  const Icon = KIND_ICON[entry.kind] ?? Circle;
  return (
    <li className="relative pl-9 before:absolute before:bottom-[-1rem] before:left-[0.8125rem] before:top-7 before:w-px before:bg-border last:before:hidden">
      <span
        aria-hidden
        className="absolute left-0 top-0 flex size-7 items-center justify-center rounded-full border bg-background text-muted-foreground"
      >
        <Icon className="size-3.5" />
      </span>
      <div className="text-xs text-muted-foreground">
        <span className="font-medium text-foreground">
          {KIND_LABEL[entry.kind] ?? entry.kind}
        </span>
        {entry.actor_name ? ` · ${entry.actor_name}` : ""}
        {" · "}
        <time dateTime={entry.at} title={new Date(entry.at).toLocaleString()}>
          {relativeTime(entry.at)}
        </time>
        {entry.note_edited_at ? " · edited" : ""}
      </div>
      {editing ? (
        <form
          className="mt-1 space-y-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (!draft.trim()) return;
            onEditNote(draft.trim());
            setEditing(false);
          }}
        >
          <Textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            aria-label="Edit note"
          />
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={!draft.trim()}>
              Save
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setDraft(entry.note_body ?? "");
                setEditing(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <p
          className={
            entry.kind === "note_added"
              ? "mt-1 whitespace-pre-wrap rounded-md border bg-muted/40 px-3 py-2 text-sm"
              : "mt-0.5 whitespace-pre-wrap text-sm"
          }
        >
          {text}
          {entry.kind === "note_added" && canEdit ? (
            <Button
              type="button"
              size="sm"
              variant="link"
              className="ml-1 h-auto p-0"
              onClick={() => setEditing(true)}
            >
              Edit
            </Button>
          ) : null}
        </p>
      )}
    </li>
  );
}
